import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import GasMeterIcon from '@mui/icons-material/GasMeter';
import SolarPowerIcon from '@mui/icons-material/SolarPower';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';

const EnergyManagement = () => {
  const energyData = [
    { type: 'Electricity', usage: '2,450 kWh', cost: '$420', change: -8.2, color: '#3b82f6', icon: <ElectricBoltIcon /> },
    { type: 'Natural Gas', usage: '1,200 kWh', cost: '$180', change: -5.1, color: '#ef4444', icon: <GasMeterIcon /> },
    { type: 'Renewable', usage: '450 kWh', cost: '$45', change: 25.3, color: '#10b981', icon: <SolarPowerIcon /> },
  ];

  const recommendations = [
    { title: 'Install LED Lighting', impact: 'Save 15% on electricity', priority: 'High' },
    { title: 'Optimize HVAC Schedule', impact: 'Reduce 20% heating/cooling', priority: 'Medium' },
    { title: 'Add Solar Panels', impact: 'Generate 30% renewable energy', priority: 'Low' },
    { title: 'Smart Thermostats', impact: 'Save 12% on HVAC', priority: 'High' },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Energy Management
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Monitor and optimize your energy consumption patterns and costs.
      </Typography>
      
      {/* Energy Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {energyData.map((energy, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Card sx={{ height: '100%', borderTop: `4px solid ${energy.color}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box sx={{ 
                    backgroundColor: `${energy.color}20`, 
                    borderRadius: '50%', 
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {React.cloneElement(energy.icon, { sx: { color: energy.color, fontSize: 24 } })}
                  </Box>
                  <Typography variant="h5" fontWeight="bold">
                    {energy.type}
                  </Typography>
                </Box>
                
                <Typography variant="h3" fontWeight="bold" gutterBottom>
                  {energy.usage}
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Monthly Cost
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {energy.cost}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {energy.change >= 0 ? (
                    <TrendingUpIcon sx={{ color: '#ef4444' }} />
                  ) : (
                    <TrendingDownIcon sx={{ color: '#10b981' }} />
                  )}
                  <Typography
                    variant="body2"
                    sx={{ color: energy.change >= 0 ? '#ef4444' : '#10b981', fontWeight: 'bold' }}
                  >
                    {energy.change >= 0 ? '+' : ''}{energy.change}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    vs last month
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Total Energy Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                  <Typography variant="h5" gutterBottom>
                    Total Energy Consumption
                  </Typography>
                  <Typography variant="h2" fontWeight="bold" color="primary">
                    4,100 kWh
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Combined electricity and gas usage
                  </Typography>
                </Box>
                <Chip 
                  label="Efficient" 
                  color="success" 
                  icon={<EnergySavingsLeafIcon />}
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Renewable Energy</Typography>
                  <Typography variant="body2" fontWeight="bold">11%</Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={11} 
                  sx={{ 
                    height: 10, 
                    borderRadius: 5,
                    backgroundColor: '#e5e7eb',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #10b981, #34d399)',
                      borderRadius: 5,
                    }
                  }}
                />
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingDownIcon sx={{ color: '#10b981', fontSize: 32 }} />
                <Box>
                  <Typography variant="h6" color="#10b981">
                    -7.2% overall reduction this month
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Saving approximately $85 compared to last month
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Peak Usage Times
              </Typography>
              <List>
                {[
                  { time: '6:00-9:00 AM', usage: 'High', icon: <WarningIcon color="warning" /> },
                  { time: '12:00-2:00 PM', usage: 'Very High', icon: <WarningIcon color="error" /> },
                  { time: '6:00-9:00 PM', usage: 'High', icon: <WarningIcon color="warning" /> },
                  { time: '10:00 PM-5:00 AM', usage: 'Low', icon: <CheckCircleIcon color="success" /> },
                ].map((item, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.time}
                      secondary={`Usage: ${item.usage}`}
                    />
                  </ListItem>
                ))}
              </List>
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#fef3c7', borderRadius: 2 }}>
                <Typography variant="body2" color="#92400e" fontWeight="medium">
                  💡 Recommendation
                </Typography>
                <Typography variant="caption" color="#92400e">
                  Shift non-essential operations to off-peak hours
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recommendations */}
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Energy Optimization Recommendations
          </Typography>
          <Grid container spacing={2}>
            {recommendations.map((rec, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6">{rec.title}</Typography>
                      <Chip 
                        label={rec.priority} 
                        size="small"
                        color={rec.priority === 'High' ? 'error' : rec.priority === 'Medium' ? 'warning' : 'success'}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {rec.impact}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Estimated savings: $120-180/month
                      </Typography>
                      <Chip 
                        label="Apply" 
                        size="small" 
                        color="primary"
                        variant="outlined"
                        clickable
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EnergyManagement;